import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import { DatePicker } from '@/components/ui/date-picker/DatePicker';
import type { AffiliateTableDateFilterType, DashboardTableDateFilter } from '@fluenceur/types';
import { Box, HStack, Popover, Portal, Separator, Text, VStack } from '@chakra-ui/react';
import type { DateValue } from '@internationalized/date';
import { parseDate } from '@internationalized/date';
import { Cart, UserPlus } from 'iconoir-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AffiliatesDateFilterProps {
  value: DashboardTableDateFilter<AffiliateTableDateFilterType>;
  onChange: (value: DashboardTableDateFilter<AffiliateTableDateFilterType>) => void;
  onClear: () => void;
}

const DATE_FILTER_TYPES: AffiliateTableDateFilterType[] = ['joinedAt', 'lastOrderAt'];

const formatDateValue = (date: DateValue | null): string | undefined => {
  if (!date) return undefined;
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
};

const parseDateString = (dateString: string | undefined): DateValue | null => {
  if (!dateString) return null;

  try {
    return parseDate(dateString);
  } catch {
    return null;
  }
};

const handleDateFilterInteractOutside = (event: Event) => {
  if ((event.target as Element)?.closest('[role="dialog"]')) {
    event.preventDefault();
  }
};

export const AffiliatesDateFilter: React.FC<AffiliatesDateFilterProps> = ({
  value,
  onChange,
  onClear,
}) => {
  const { t } = useTranslation();
  const currentType = value.dateFilterType ?? 'joinedAt';
  const [tempType, setTempType] = useState<AffiliateTableDateFilterType>(currentType);
  const [tempStartDate, setTempStartDate] = useState<DateValue | null>(
    parseDateString(value.startDate)
  );
  const [tempEndDate, setTempEndDate] = useState<DateValue | null>(parseDateString(value.endDate));

  const typeOptions = useMemo(
    () =>
      DATE_FILTER_TYPES.map((type) => ({
        value: type,
        label: t(`affiliates.filters.date.types.${type}`),
        icon:
          type === 'joinedAt' ? (
            <UserPlus width={15} height={15} aria-hidden />
          ) : (
            <Cart width={15} height={15} aria-hidden />
          ),
      })),
    [t]
  );

  useEffect(() => {
    setTempType(currentType);
    setTempStartDate(parseDateString(value.startDate));
    setTempEndDate(parseDateString(value.endDate));
  }, [currentType, value.endDate, value.startDate]);

  const handleApply = () => {
    let finalEndDate = tempEndDate;
    if (tempStartDate && tempEndDate && tempEndDate.compare(tempStartDate) < 0) {
      finalEndDate = tempStartDate;
      setTempEndDate(finalEndDate);
    }

    onChange({
      dateFilterType: tempType,
      startDate: formatDateValue(tempStartDate),
      endDate: formatDateValue(finalEndDate),
    });
  };

  const handleClear = () => {
    setTempType('joinedAt');
    setTempStartDate(null);
    setTempEndDate(null);
    onClear();
  };

  const isFilterActive = Boolean(value.startDate || value.endDate);
  const activeTypeLabel = t(`affiliates.filters.date.types.${currentType}`);
  const activeRange =
    value.startDate && value.endDate
      ? `${value.startDate} - ${value.endDate}`
      : (value.startDate ?? value.endDate);

  return (
    <FilterButton
      isFilterActive={isFilterActive}
      onClearFilter={handleClear}
      onInteractOutside={handleDateFilterInteractOutside}
    >
      <FilterButtonAction>
        <FilterButtonLabel>{t('affiliates.filters.date.label')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>
            {activeTypeLabel}: {activeRange}
          </FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} w="280px">
            <FilterButtonMenuHeader
              title={t('affiliates.filters.date.title')}
              onClearAll={handleClear}
            />
            <Box p={3}>
              <VStack gap={3} align="stretch" onClick={(event) => event.stopPropagation()}>
                <VStack gap={0.5} align="stretch">
                  {typeOptions.map((option) => (
                    <FilterMenuItem
                      key={option.value}
                      onClick={() => setTempType(option.value)}
                      isSelected={tempType === option.value}
                    >
                      <HStack gap={2} minW={0}>
                        {option.icon}
                        <Text fontSize="sm" truncate>
                          {option.label}
                        </Text>
                      </HStack>
                    </FilterMenuItem>
                  ))}
                </VStack>
                <Separator />
                <VStack gap={3} align="stretch">
                  <DatePicker
                    label={t('filters.dateRange.startDate')}
                    value={tempStartDate}
                    onChange={setTempStartDate}
                  />
                  <DatePicker
                    label={t('filters.dateRange.endDate')}
                    value={tempEndDate}
                    onChange={setTempEndDate}
                    minValue={tempStartDate || undefined}
                  />
                </VStack>
              </VStack>
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
